import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo } from 'react'
import { useFieldArray, useForm, useWatch } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { z } from 'zod'

import CopyButtonWithLabel from '@/common/CopyButtonWithLabel'
import GradientWrapper from '@/common/GradientWrapper'
import { downloadJson } from '@/helpers'
import ProposalPreview from '@/pages/Multisig/components/ProposalPreview'
import { useSubmitMultisigProposal } from '@/pages/Multisig/CreateProposal/hooks/submit-multisig-proposal'
import { UiButton } from '@/ui/UiButton'
import { UiCheckbox } from '@/ui/UiCheckbox'
import { UiInput } from '@/ui/UiInput'
import { UiLabel } from '@/ui/UiLabel'

import { buildStartEpochMessage } from './epochMessageBuilder'

const tssInfoSchema = z.object({
  certificate: z.string().min(1, 'Certificate is required'),
  domain: z.string().min(1, 'Domain is required'),
  address: z.string().min(1, 'Address is required'),
  active: z.boolean(),
})

const startEpochSchema = z.object({
  creator: z.string().min(1, 'Creator address is required'),
  epochId: z
    .string()
    .refine(value => !isNaN(Number(value)) && Number(value) >= 0, {
      message: 'Epoch ID must be a valid non-negative number',
    }),
  tssThreshold: z
    .string()
    .min(1, 'TSS threshold is required')
    .refine(value => !isNaN(Number(value)) && Number(value) >= 0, {
      message: 'TSS threshold must be a valid non-negative number',
    }),
  tssInfo: z.array(tssInfoSchema).min(1, 'At least one TSS info is required'),
})

export default function StartEpochForm() {
  const { account } = useParams<{ account: string }>()

  const form = useForm({
    resolver: zodResolver(startEpochSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    defaultValues: {
      creator: '',
      epochId: '',
      tssThreshold: '',
      tssInfo: [
        {
          certificate: '',
          domain: '',
          address: '',
          active: true,
        },
      ],
    },
  })

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'tssInfo',
  })

  const submitProposalTx = useSubmitMultisigProposal({ group: account ?? '' })

  useEffect(() => {
    form.setValue('creator', account ?? '', { shouldValidate: true })
  }, [account, form])

  const watchedValues = useWatch({ control: form.control })

  const messagePayload = useMemo(() => {
    if (!form.formState.isValid || !watchedValues) return null

    try {
      return buildStartEpochMessage({
        creator: watchedValues.creator ?? '',
        epochId: watchedValues.epochId ?? '',
        tssThreshold: watchedValues.tssThreshold ?? '',
        tssInfo:
          watchedValues.tssInfo?.map(info => ({
            certificate: info?.certificate ?? '',
            domain: info?.domain ?? '',
            address: info?.address ?? '',
            active: info?.active ?? true,
          })) ?? [],
      })
    } catch {
      return null
    }
  }, [form.formState.isValid, watchedValues])

  const generatedJson = useMemo(() => {
    if (!messagePayload) return null
    return JSON.stringify(messagePayload, null, 2)
  }, [messagePayload])

  const isSubmitting = submitProposalTx.isPending
  const isSubmitDisabled = !form.formState.isValid || isSubmitting

  return (
    <div className='space-y-6'>
      <div className='space-y-2'>
        <h3 className='text-foreground text-lg font-semibold'>Start Epoch</h3>
        <p className='text-muted-foreground text-sm'>
          Fill in the form to start a new epoch
        </p>
      </div>

      <form
        onSubmit={event => event.preventDefault()}
        className='flex flex-col gap-6'
      >
        <div className='flex flex-col gap-3'>
          <UiLabel htmlFor='creator'>Creator Address</UiLabel>
          <UiInput
            id='creator'
            {...form.register('creator')}
            placeholder='Connect wallet'
            readOnly
            disabled={isSubmitting}
            className='bg-input border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
          />
          {form.formState.errors.creator && (
            <p className='text-destructive text-sm'>
              {form.formState.errors.creator.message}
            </p>
          )}
        </div>

        <div className='flex flex-col gap-3'>
          <UiLabel htmlFor='epochId'>Epoch ID</UiLabel>
          <GradientWrapper>
            <UiInput
              id='epochId'
              {...form.register('epochId')}
              placeholder='Enter epoch ID'
              disabled={isSubmitting}
              className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
            />
          </GradientWrapper>
          {form.formState.errors.epochId && (
            <p className='text-destructive text-sm'>
              {form.formState.errors.epochId.message}
            </p>
          )}
        </div>

        <div className='flex flex-col gap-3'>
          <UiLabel htmlFor='tssThreshold'>TSS Threshold</UiLabel>
          <GradientWrapper>
            <UiInput
              id='tssThreshold'
              {...form.register('tssThreshold')}
              placeholder='Enter TSS threshold'
              disabled={isSubmitting}
              className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
            />
          </GradientWrapper>
          {form.formState.errors.tssThreshold && (
            <p className='text-destructive text-sm'>
              {form.formState.errors.tssThreshold.message}
            </p>
          )}
        </div>

        <div className='flex flex-col gap-4'>
          <div className='flex items-center justify-between'>
            <UiLabel>TSS Info</UiLabel>
            <UiButton
              type='button'
              variant='outline'
              size='sm'
              onClick={() =>
                append({
                  certificate: '',
                  domain: '',
                  address: '',
                  active: true,
                })
              }
            >
              Add TSS Info
            </UiButton>
          </div>

          {fields.map((field, index) => (
            <div key={field.id} className='border-border rounded-lg border p-4'>
              <div className='mb-3 flex items-center justify-between'>
                <h4 className='text-foreground font-medium'>
                  TSS Info #{index + 1}
                </h4>
                {fields.length > 1 && (
                  <UiButton
                    type='button'
                    variant='ghost'
                    size='sm'
                    onClick={() => remove(index)}
                  >
                    Remove
                  </UiButton>
                )}
              </div>

              <div className='flex flex-col gap-3'>
                <div className='flex flex-col gap-2'>
                  <UiLabel htmlFor={`tssInfo.${index}.certificate`}>
                    Certificate
                  </UiLabel>
                  <GradientWrapper>
                    <UiInput
                      id={`tssInfo.${index}.certificate`}
                      {...form.register(`tssInfo.${index}.certificate`)}
                      placeholder='Enter certificate'
                      disabled={isSubmitting}
                      className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                    />
                  </GradientWrapper>
                  {form.formState.errors.tssInfo?.[index]?.certificate && (
                    <p className='text-destructive text-sm'>
                      {
                        form.formState.errors.tssInfo[index]?.certificate
                          ?.message
                      }
                    </p>
                  )}
                </div>

                <div className='flex flex-col gap-2'>
                  <UiLabel htmlFor={`tssInfo.${index}.domain`}>Domain</UiLabel>
                  <GradientWrapper>
                    <UiInput
                      id={`tssInfo.${index}.domain`}
                      {...form.register(`tssInfo.${index}.domain`)}
                      placeholder='Enter domain'
                      disabled={isSubmitting}
                      className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                    />
                  </GradientWrapper>
                  {form.formState.errors.tssInfo?.[index]?.domain && (
                    <p className='text-destructive text-sm'>
                      {form.formState.errors.tssInfo[index]?.domain?.message}
                    </p>
                  )}
                </div>

                <div className='flex flex-col gap-2'>
                  <UiLabel htmlFor={`tssInfo.${index}.address`}>
                    Address
                  </UiLabel>
                  <GradientWrapper>
                    <UiInput
                      id={`tssInfo.${index}.address`}
                      {...form.register(`tssInfo.${index}.address`)}
                      placeholder='Enter address'
                      disabled={isSubmitting}
                      className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                    />
                  </GradientWrapper>
                  {form.formState.errors.tssInfo?.[index]?.address && (
                    <p className='text-destructive text-sm'>
                      {form.formState.errors.tssInfo[index]?.address?.message}
                    </p>
                  )}
                </div>

                <div className='flex items-center space-x-2'>
                  <UiCheckbox
                    id={`tssInfo.${index}.active`}
                    checked={form.watch(`tssInfo.${index}.active`)}
                    onCheckedChange={checked =>
                      form.setValue(`tssInfo.${index}.active`, !!checked)
                    }
                  />
                  <UiLabel htmlFor={`tssInfo.${index}.active`}>Active</UiLabel>
                </div>
              </div>
            </div>
          ))}

          {form.formState.errors.tssInfo && (
            <p className='text-destructive text-sm'>
              {form.formState.errors.tssInfo.message}
            </p>
          )}
        </div>
      </form>

      {messagePayload && <ProposalPreview messages={messagePayload.messages} />}

      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex flex-wrap items-center gap-2'>
          {generatedJson && (
            <>
              <CopyButtonWithLabel
                value={generatedJson}
                label='Copy JSON'
                copiedLabel='Copied'
                tooltip='Copy JSON'
              />
              <UiButton
                type='button'
                variant='outline'
                size='sm'
                onClick={() =>
                  downloadJson(messagePayload, 'start-epoch-message.json')
                }
                disabled={isSubmitting}
              >
                Download JSON
              </UiButton>
            </>
          )}
        </div>
        <UiButton
          type='button'
          disabled={isSubmitDisabled}
          onClick={form.handleSubmit(values => {
            const messageObject = buildStartEpochMessage({
              creator: values.creator,
              epochId: values.epochId,
              tssThreshold: values.tssThreshold,
              tssInfo: values.tssInfo.map(info => ({
                certificate: info.certificate,
                domain: info.domain,
                address: info.address,
                active: info.active,
              })),
            })
            submitProposalTx.mutate({
              rawJsonText: JSON.stringify(messageObject, null, 2),
            })
          })}
        >
          {isSubmitting ? 'Submitting…' : 'Submit proposal'}
        </UiButton>
      </div>
    </div>
  )
}
